import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil } from 'lucide-react';
import { DataTable, type Column } from '@/components/shared/DataTable';
import { useCurrency } from '@/hooks/useCurrency';
import type {
  ForecastReviewComparisonResponse,
  ForecastReviewComparisonRow,
} from '@/services/forecastReviewService';
import {
  ClassificationBadges,
  ColumnHeader,
  DeviationCell,
  SalesWithTrendCell,
  SeverityBadge,
} from './ForecastReviewTableCells';

export interface ComparisonTablePagination {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
}

interface ComparisonTableProps {
  data: ForecastReviewComparisonResponse | null;
  loading?: boolean;
  viewType: 'units' | 'value';
  level: 'product' | 'family' | 'brand' | 'supplier';
  pagination: ComparisonTablePagination;
  onLevelChange: (l: 'product' | 'family' | 'brand' | 'supplier') => void;
  onApplyChartFilter?: (row: ForecastReviewComparisonRow) => void;
  activeElementId?: string | null;
  modifyManualEnabled?: boolean;
  onModifyForecast?: (row: ForecastReviewComparisonRow) => void;
  modifyingProductId?: number | null;
  warehouseSelected?: boolean;
}

function statusBadgeClass(status: string | null | undefined): string {
  if (!status) return 'bg-gray-100 text-gray-600';
  if (status === 'new_product' || status === 'seasonal') return 'bg-blue-100 text-blue-800';
  if (status === 'on_demand') return 'bg-violet-100 text-violet-800';
  if (status === 'obsolete_with_forecast') return 'bg-red-100 text-red-800';
  if (status === 'no_aa_base' || status === 'no_recent_consumption') return 'bg-amber-100 text-amber-800';
  if (status === 'new_consumption_expected') return 'bg-emerald-100 text-emerald-800';
  return 'bg-gray-100 text-gray-600';
}

function ReferenceStatusBadge({ status }: { status: string | null | undefined }) {
  const { t } = useTranslation();
  if (!status) return null;
  const label = t(`forecastReview.comparison.statusLabels.${status}`, status);
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium ${statusBadgeClass(status)}`}>
      {label}
    </span>
  );
}

function formatTrendTooltip(
  points: Array<{ period: string; value: number }>,
  formatValue: (value: number) => string,
): string {
  return points.map((p) => `${p.period}: ${formatValue(p.value)}`).join(' · ');
}

function ComparisonStatusCell({
  lastYearStatus,
  recentStatus,
}: {
  lastYearStatus: string | null | undefined;
  recentStatus: string | null | undefined;
}) {
  if (!lastYearStatus && !recentStatus) return <span className="text-gray-400">—</span>;
  if (lastYearStatus && lastYearStatus === recentStatus) {
    return <ReferenceStatusBadge status={lastYearStatus} />;
  }
  return (
    <div className="flex max-w-[8rem] flex-col gap-1">
      <ReferenceStatusBadge status={lastYearStatus} />
      <ReferenceStatusBadge status={recentStatus} />
    </div>
  );
}

export function ComparisonTable({
  data,
  loading,
  viewType,
  level,
  pagination,
  onLevelChange,
  onApplyChartFilter,
  activeElementId,
  modifyManualEnabled = false,
  onModifyForecast,
  modifyingProductId = null,
  warehouseSelected = true,
}: ComparisonTableProps) {
  const { t } = useTranslation();
  const { formatViewAmount } = useCurrency();
  const formatAmount = (value: number | null | undefined) =>
    value == null ? '—' : formatViewAmount(value, viewType);
  const formatTrendValue = (value: number) => formatViewAmount(value, viewType);

  const isProductLevel = level === 'product';
  const forecastPeriodLabel = data?.forecast_period_label ?? '—';
  const lyPeriodLabel = data?.last_year_period_label ?? '—';
  const recentPeriodLabel = data?.recent_period_label ?? '—';

  const levels: Array<{ id: typeof level; label: string }> = [
    { id: 'product', label: t('forecastReview.comparison.levelProduct') },
    { id: 'family', label: t('forecastReview.comparison.levelFamily') },
    { id: 'brand', label: t('forecastReview.comparison.levelBrand') },
    { id: 'supplier', label: t('forecastReview.comparison.levelSupplier') },
  ];

  const columns = useMemo((): Column[] => {
    const base: Column[] = [
      {
        header: (
          <ColumnHeader
            title={
              isProductLevel
                ? t('forecastReview.comparison.product')
                : t('forecastReview.comparison.element')
            }
            align="left"
          />
        ),
        exportHeader: t('forecastReview.comparison.product'),
        accessor: 'element_name',
        cell: (_: string, item: ForecastReviewComparisonRow) => (
          <div className="min-w-[8rem]">
            <div
              className={`font-medium ${onApplyChartFilter ? 'text-[#1681ba] hover:underline' : 'text-gray-900'}`}
            >
              {item.element_name ?? '—'}
            </div>
            {isProductLevel && (item.element_code || item.family_name) ? (
              <div className="text-xs text-gray-500">
                {item.element_code ? <span>{item.element_code}</span> : null}
                {item.element_code && item.family_name ? (
                  <span className="text-gray-400"> · </span>
                ) : null}
                {item.family_name ? <span className="text-gray-600">{item.family_name}</span> : null}
              </div>
            ) : item.element_code ? (
              <div className="text-xs text-gray-500">{item.element_code}</div>
            ) : null}
            {onApplyChartFilter ? (
              <div className="text-xs text-gray-400">{t('forecastReview.comparison.filterHint')}</div>
            ) : null}
            {item.product_count && item.product_count > 1 ? (
              <div className="text-xs text-gray-400">
                {t('forecastReview.comparison.productCount', { count: item.product_count })}
              </div>
            ) : null}
          </div>
        ),
      },
    ];

    if (isProductLevel) {
      base.push({
        header: <ColumnHeader title={t('forecastReview.comparison.supplier')} align="left" />,
        exportHeader: t('forecastReview.comparison.supplier'),
        accessor: 'supplier_name',
        cell: (value: string | null | undefined) => (
          <span className="text-gray-700">{value ?? '—'}</span>
        ),
      });
      base.push({
        header: <ColumnHeader title={t('forecastReview.comparison.aiModel')} align="left" />,
        exportHeader: t('forecastReview.comparison.aiModel'),
        accessor: 'ai_model_name',
        cell: (value: string | null | undefined) => (
          <span className="text-gray-700">{value ?? '—'}</span>
        ),
      });
    }

    base.push(
      {
        header: (
          <ColumnHeader
            title={t('forecastReview.comparison.forecast')}
            subtitle={forecastPeriodLabel}
            align="right"
          />
        ),
        exportHeader: t('forecastReview.comparison.forecast'),
        accessor: 'forecast_total',
        align: 'right' as const,
        numeric: true,
        cell: (_: unknown, item: ForecastReviewComparisonRow) => (
          <div className="flex flex-col items-end leading-tight">
            <span className="font-medium text-gray-900">{formatAmount(item.forecast_total)}</span>
            {item.forecast_monthly_average != null ? (
              <span className="text-[10px] text-gray-500">
                {t('forecastReview.comparison.forecastMonthlyAvg', {
                  value: formatAmount(item.forecast_monthly_average),
                })}
              </span>
            ) : null}
          </div>
        ),
      },
      {
        header: (
          <ColumnHeader
            title={t('forecastReview.comparison.lastYear')}
            subtitle={lyPeriodLabel}
            align="right"
          />
        ),
        exportHeader: t('forecastReview.comparison.lastYear'),
        accessor: 'last_year_total',
        width: '108px',
        align: 'right' as const,
        cell: (_: unknown, item: ForecastReviewComparisonRow) => (
          <SalesWithTrendCell
            total={item.last_year_total}
            trend={isProductLevel ? item.last_year_trend : undefined}
            viewType={viewType}
            trendTitle={
              item.last_year_trend?.length
                ? formatTrendTooltip(item.last_year_trend, formatTrendValue)
                : undefined
            }
          />
        ),
      },
      {
        header: <ColumnHeader title={t('forecastReview.comparison.deviationAa')} align="right" />,
        exportHeader: t('forecastReview.comparison.deviationAa'),
        accessor: 'last_year_diff_qty',
        align: 'right' as const,
        cell: (_: unknown, item: ForecastReviewComparisonRow) => (
          <DeviationCell
            diffQty={item.last_year_diff_qty}
            variationPct={item.last_year_variation_pct}
            viewType={viewType}
          />
        ),
      },
      {
        header: (
          <ColumnHeader
            title={t('forecastReview.comparison.recent')}
            subtitle={recentPeriodLabel}
            align="right"
          />
        ),
        exportHeader: t('forecastReview.comparison.recent'),
        accessor: 'recent_total',
        width: '108px',
        align: 'right' as const,
        cell: (_: unknown, item: ForecastReviewComparisonRow) => (
          <SalesWithTrendCell
            total={item.recent_total}
            trend={isProductLevel ? item.recent_trend : undefined}
            viewType={viewType}
            trendTitle={
              item.recent_trend?.length ? formatTrendTooltip(item.recent_trend, formatTrendValue) : undefined
            }
          />
        ),
      },
      {
        header: <ColumnHeader title={t('forecastReview.comparison.deviationRecent')} align="right" />,
        exportHeader: t('forecastReview.comparison.deviationRecent'),
        accessor: 'recent_diff_qty',
        align: 'right' as const,
        cell: (_: unknown, item: ForecastReviewComparisonRow) => (
          <DeviationCell
            diffQty={item.recent_diff_qty}
            variationPct={item.recent_variation_pct}
            viewType={viewType}
          />
        ),
      },
      {
        header: <ColumnHeader title={t('forecastReview.comparison.severity')} align="left" />,
        exportHeader: t('forecastReview.comparison.severity'),
        accessor: 'severity',
        cell: (_: unknown, item: ForecastReviewComparisonRow) => (
          <SeverityBadge severity={item.severity} />
        ),
      },
      {
        header: <ColumnHeader title={t('forecastReview.comparison.status')} align="left" />,
        exportHeader: t('forecastReview.comparison.status'),
        accessor: 'last_year_status',
        cell: (_: unknown, item: ForecastReviewComparisonRow) => (
          <ComparisonStatusCell
            lastYearStatus={item.last_year_status}
            recentStatus={item.recent_status}
          />
        ),
      },
      {
        header: <ColumnHeader title={t('forecastReview.comparison.classification')} align="center" />,
        exportHeader: t('forecastReview.comparison.classification'),
        accessor: 'classification',
        align: 'center' as const,
        cell: (_: unknown, item: ForecastReviewComparisonRow) => (
          <ClassificationBadges classification={item.classification} />
        ),
      }
    );

    if (isProductLevel && onModifyForecast) {
      base.push({
        header: <ColumnHeader title={t('forecastReview.comparison.actions')} align="right" />,
        exportHeader: t('forecastReview.comparison.actions'),
        accessor: 'element_id',
        align: 'right' as const,
        cell: (_: number, item: ForecastReviewComparisonRow) => (
          <div className="flex justify-end">
            <button
              type="button"
              disabled={
                !modifyManualEnabled || !warehouseSelected || modifyingProductId === item.element_id
              }
              onClick={(e) => {
                e.stopPropagation();
                if (modifyManualEnabled) onModifyForecast(item);
              }}
              className="inline-flex items-center gap-1 rounded border border-[#1681ba]/40 bg-white px-2 py-1 text-xs text-[#1681ba] hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50"
              title={
                !modifyManualEnabled
                  ? t('forecastReview.comparison.editForecastDisabledFlag')
                  : !warehouseSelected
                    ? t('forecastReview.comparison.editForecastNeedsWarehouse')
                    : t('forecastReview.comparison.editForecast')
              }
            >
              <Pencil className="h-3 w-3" />
              {t('forecastReview.comparison.editForecastShort')}
            </button>
          </div>
        ),
      });
    }

    return base;
  }, [
    t,
    viewType,
    formatViewAmount,
    isProductLevel,
    forecastPeriodLabel,
    lyPeriodLabel,
    recentPeriodLabel,
    onApplyChartFilter,
    modifyManualEnabled,
    onModifyForecast,
    modifyingProductId,
    warehouseSelected,
  ]);

  const rows = data?.results ?? [];

  return (
    <div className="space-y-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-medium text-gray-900">{t('forecastReview.comparison.title')}</h3>
          {data ? (
            <p className="mt-0.5 max-w-3xl text-xs leading-relaxed text-gray-500">
              {t('forecastReview.comparison.subtitleDual', {
                forecastPeriod: data.forecast_period_label,
                lyPeriod: data.last_year_period_label,
                recentPeriod: data.recent_period_label,
              })}
            </p>
          ) : null}
          {onApplyChartFilter ? (
            <p className="mt-1 text-xs text-[#1681ba]">{t('forecastReview.comparison.filterHintRow')}</p>
          ) : null}
        </div>
        <div className="inline-flex rounded-md border border-gray-200 p-0.5 text-xs">
          {levels.map((l) => (
            <button
              key={l.id}
              type="button"
              onClick={() => onLevelChange(l.id)}
              className={`rounded px-2.5 py-1 ${level === l.id ? 'bg-gray-100 font-medium text-gray-900' : 'text-gray-600'}`}
            >
              {l.label}
            </button>
          ))}
        </div>
      </div>

      {loading && rows.length === 0 ? (
        <div className="py-12 text-center text-sm text-gray-500">{t('forecastReview.comparison.loading')}</div>
      ) : !loading && rows.length === 0 ? (
        <div className="py-12 text-center text-sm text-gray-500">{t('forecastReview.comparison.empty')}</div>
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          compact
          resizableColumns
          loading={loading}
          pagination
          onRowClick={onApplyChartFilter}
          rowClassName={(row: ForecastReviewComparisonRow) =>
            activeElementId && String(row.element_id) === activeElementId
              ? 'bg-blue-50 ring-1 ring-inset ring-blue-200'
              : ''
          }
          externalPagination={{
            currentPage: pagination.currentPage,
            totalPages: pagination.totalPages,
            totalCount: pagination.totalCount,
            pageSize: pagination.pageSize,
            onPageChange: pagination.onPageChange,
            onPageSizeChange: pagination.onPageSizeChange,
          }}
        />
      )}
    </div>
  );
}

